import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UseControlledTextParams = {
  onCancel?: () => void
  onSubmit: (value: string) => void
  persistedValue: string
  required?: boolean
}

export const useControlledText = ({
  onCancel,
  onSubmit,
  persistedValue,
  required = false,
}: UseControlledTextParams) => {
  const [editedValue, setEditedValue] = useState(persistedValue)
  const isSubmitting = useRef(false)

  const isDirty = useMemo(() => {
    return editedValue !== persistedValue
  }, [editedValue, persistedValue])

  const isValid = useMemo(() => {
    return required ? editedValue.length > 0 : true
  }, [editedValue, required])

  /**
   * After we submit, the server cache will eventually be updated
   * and rerender with the updated `persistedValue`. When we see
   * this update, we can set our `isSubmitting` back to false.
   */
  useEffect(() => {
    if (isSubmitting.current === true && persistedValue === editedValue) {
      isSubmitting.current = false
    }
  }, [editedValue, persistedValue])

  /**
   * Fallback used to reset `editedValue` back to `persistedValue`
   * where `persistedValue` has a value but `editedValue` does not
   * for instances outside of user edits.
   */
  useEffect(() => {
    if (persistedValue) {
      setEditedValue((value) => (value === '' ? persistedValue : value))
    }
  }, [persistedValue])

  /**
   * Used to reset the edited value back to the persisted value.
   */
  const cancelEdits = useCallback(() => {
    setEditedValue(persistedValue)
    onCancel?.()
  }, [onCancel, persistedValue])

  /**
   * Used to update the client side value of the field.
   * This is typically used in the field's onChange event.
   */
  const updateEditedValue = useCallback((value: string) => {
    setEditedValue(value)
  }, [])

  /**
   * Calls provided `onSubmit` function with our edited value,
   * but ONLY if the value has changed. Otherwise, it will perform
   * a cancelEdits to ensure state is cleaned up.
   */
  const submit = useCallback(() => {
    // If we're already in the process of submitting, do nothing.
    if (isSubmitting.current === true) return

    if (isDirty && isValid) {
      isSubmitting.current = true
      onSubmit(editedValue)
    } else {
      onCancel?.()
      cancelEdits()
    }
  }, [cancelEdits, editedValue, isDirty, isValid, onCancel, onSubmit])

  return {
    cancelEdits,
    editedValue,
    isDirty,
    submit,
    updateEditedValue,
  }
}
